import {
  ACTIVITY_EMISSION_SIGNALS,
  HIGHER_EMISSION_INTENSITY_SECTORS,
} from '../../data/assessment/sectorMappings'
import { APPLICABILITY_RULES } from '../../data/regulatory/applicabilityRules'
import { REGULATORY_SOURCES } from '../../data/regulatory/sources'
import type {
  ApplicabilityCondition,
  ApplicabilityResult,
  ApplicabilityState,
  AssessmentState,
  Sector,
} from './types'

function listingOrExchange(state: AssessmentState): string {
  const fromProfile = state.companyProfile.listingStatus
  const fromResponse = String(state.responses.rc_listed_exchange || '')
  if (fromResponse && fromResponse !== 'no' && fromResponse !== 'not_sure') return fromResponse
  if (fromProfile === 'adx' || fromProfile === 'dfm' || fromProfile === 'both') return fromProfile
  return fromResponse || fromProfile || ''
}

function likelyEmitterProfile(state: AssessmentState): string {
  const sector = state.companyProfile.sector as Sector | ''
  if (sector && HIGHER_EMISSION_INTENSITY_SECTORS.includes(sector)) return 'yes'
  const activities = state.companyProfile.businessActivities || []
  if (activities.some((a) => (ACTIVITY_EMISSION_SIGNALS as readonly string[]).includes(a))) return 'yes'
  if (state.carbon) {
    const hasExistingSource = Object.values(state.carbon).some((c) => c.exists === 'yes')
    if (hasExistingSource) return 'yes'
  }
  return 'no'
}

export function buildApplicabilityContext(state: AssessmentState): Record<string, string> {
  const footprint = state.companyProfile.operatingFootprint
  const multiEmirate =
    state.responses.rc_multi_emirate ||
    (footprint === 'multi_emirate' || footprint === 'uae_and_international' ? 'yes' : '')

  return {
    listingOrExchange: listingOrExchange(state),
    ipo: String(
      state.responses.rc_ipo || (state.companyProfile.listingStatus === 'preparing_ipo' ? 'yes' : 'no'),
    ),
    operatesInUae: state.companyProfile.emirate ? 'yes' : 'not_sure',
    likelyEmitterProfile: likelyEmitterProfile(state),
    designatedMrv: String(state.responses.rc_designated_mrv || ''),
    authorityNotice: String(state.responses.rc_authority_notice || ''),
    multiEmirate: String(multiEmirate || ''),
    customerRequests: String(
      state.responses.st_customers ||
        state.responses.rc_customer_requests ||
        state.companyProfile.customersRequestEsg ||
        '',
    ),
    multinationalSupply: String(state.responses.rc_multinational_supply || ''),
    lenderRequests: String(state.responses.st_lenders || ''),
    parentReporting: String(state.responses.rc_parent_reporting || ''),
    exportEu: String(
      state.responses.rc_export_eu ||
        ((state.companyProfile.exportMarkets || []).includes('eu') ? 'yes' : ''),
    ),
    contractualEmissions: String(state.responses.rc_contractual_emissions || ''),
    hasGhgInventory: String(state.responses.rc_has_ghg_inventory || ''),
    framework: String(state.responses.rc_framework || ''),
  }
}

function conditionMet(condition: ApplicabilityCondition, ctx: Record<string, string>): boolean {
  const value = ctx[condition.field] || ''
  if (condition.equals != null) {
    const expected = Array.isArray(condition.equals) ? condition.equals : [condition.equals]
    return expected.includes(value)
  }
  if (condition.includesAny) {
    return condition.includesAny.some((v) => value === v || value.includes(v))
  }
  return false
}

const STATE_RANK: Record<ApplicabilityState, number> = {
  likely_relevant: 5,
  further_verification_required: 4,
  potentially_relevant: 3,
  voluntary_or_market_driven: 2,
  no_trigger_identified: 1,
}

/** Prefer more specific notes when rank ties (longer / later rules win on equal rank). */
export function evaluateApplicability(state: AssessmentState): ApplicabilityResult[] {
  const ctx = buildApplicabilityContext(state)
  const byRef = new Map<string, ApplicabilityResult>()

  for (const source of REGULATORY_SOURCES) {
    byRef.set(source.id, {
      referenceId: source.id,
      title: source.title,
      authority: source.authority,
      url: source.url,
      state: 'no_trigger_identified',
      notes:
        'No trigger identified from supplied information. This does not prove the reference is irrelevant.',
      lastReviewed: source.lastReviewed,
    })
  }

  for (const rule of APPLICABILITY_RULES) {
    const matched = rule.conditions.every((c) => conditionMet(c, ctx))
    if (!matched) continue
    const current = byRef.get(rule.referenceId)
    if (!current) continue
    if (STATE_RANK[rule.resultIfMatched] > STATE_RANK[current.state]) {
      byRef.set(rule.referenceId, {
        ...current,
        state: rule.resultIfMatched,
        notes: rule.notes,
      })
    } else if (STATE_RANK[rule.resultIfMatched] === STATE_RANK[current.state]) {
      // Keep the more specific note when ranks are equal.
      if (rule.notes.length >= current.notes.length) {
        byRef.set(rule.referenceId, {
          ...current,
          state: rule.resultIfMatched,
          notes: rule.notes,
        })
      }
    }
  }

  // Keep major voluntary frameworks visible for exploration when nothing matched.
  for (const id of ['gri_standards', 'ifrs_sustainability', 'ghg_protocol']) {
    const item = byRef.get(id)
    if (item && item.state === 'no_trigger_identified') {
      byRef.set(id, {
        ...item,
        state: 'voluntary_or_market_driven',
        notes:
          item.notes +
          ' Shown as a voluntary methodology/framework reference for companies exploring structured disclosure.',
      })
    }
  }

  return [...byRef.values()].sort(
    (a, b) => STATE_RANK[b.state] - STATE_RANK[a.state] || a.title.localeCompare(b.title),
  )
}

export function applicabilityLabel(state: ApplicabilityState): string {
  switch (state) {
    case 'likely_relevant':
      return 'Likely relevant based on supplied information'
    case 'potentially_relevant':
      return 'Potentially relevant'
    case 'further_verification_required':
      return 'Further verification required'
    case 'voluntary_or_market_driven':
      return 'Voluntary or market-driven consideration'
    case 'no_trigger_identified':
      return 'No trigger identified from supplied information'
  }
}
